import { useQuery } from '@tanstack/react-query';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import type { CartLineInput } from 'services/shopify';
import { ShopifyService } from 'services/shopify';
import type { Cart } from 'types/cart/Cart';
import type { PromoCode } from 'types/cart/PromoCode';
import { convertToCountryCodeEnum } from 'utils/convertToCountryCodeEnum';
import { mapShopifyCart } from 'utils/mappers/mapShopifyCart';

/* INFO: We create a verbose cache key using the lines and codes
 * for the SupplementPicker which needs to create two carts for
 * one block. */
export const createCacheKey = (
  lines?: CartLineInput[],
  promoCodes?: PromoCodes,
): string => {
  const linesKey =
    lines
      ?.map(({ merchandiseId, sellingPlanId }) => {
        const mId = merchandiseId.split('/').pop();
        const sId = sellingPlanId ? `:${sellingPlanId.split('/').pop()}` : '';
        return `${mId}${sId}`;
      })
      .sort()
      .join('-') || '';

  const codesKey =
    promoCodes
      ?.map((p) => p?.code)
      .filter(Boolean)
      .sort()
      .join('-') || '';

  return `${linesKey}:${codesKey}`;
};

export type PromoCodes = Array<PromoCode | null | undefined>;

interface CreateDummyCartParams {
  lines: CartLineInput[] | undefined;
  promoCodes: PromoCodes | undefined;
  countryCode: string;
}

export async function createDummyCart({
  lines,
  promoCodes,
  countryCode,
}: CreateDummyCartParams): Promise<Cart | null> {
  const { cartCreate } = await ShopifyService.createCart({
    input: {
      buyerIdentity: {
        countryCode: convertToCountryCodeEnum(countryCode),
      },
      lines,
    },
  });

  if (cartCreate?.cart?.id) {
    if (promoCodes && promoCodes?.length) {
      const { cartDiscountCodesUpdate } = await ShopifyService.addDiscount({
        cartId: cartCreate.cart.id,
        discountCodes: promoCodes.reduce<string[]>(
          (acc, curr) => (curr ? [...acc, curr.code] : acc),
          [],
        ),
      });

      if (cartDiscountCodesUpdate?.cart) {
        return mapShopifyCart(cartDiscountCodesUpdate.cart);
      }
    } else {
      return mapShopifyCart(cartCreate.cart);
    }
  }

  return null;
}

export interface UseDummyCartOptions {
  key: string;
  id: string;
  lines?: CartLineInput[];
  promoCodes?: PromoCodes;
  disable?: boolean;
}

export const useDummyCart = ({
  key,
  id,
  lines,
  promoCodes,
  disable,
}: UseDummyCartOptions) => {
  const {
    value: { countryCode },
    isLoading: isLocaleLoading,
  } = useLocalStorage(UserLocale);

  return useQuery({
    queryKey: [key, id, countryCode, createCacheKey(lines, promoCodes)],
    queryFn: () => createDummyCart({ lines, promoCodes, countryCode }),
    enabled: !!lines?.length && !isLocaleLoading && !disable,
  });
};
